/**
 * diagram.tsx — a tiny, dependency-free diagram renderer that feels native to the
 * studio. AI prose can embed a fenced ```flow / ```ladder / ```lineage / ```diagram
 * / ```mermaid block; this turns it into node chips joined by labelled connectors,
 * drawn with the same phosphor/vermilion/ink tokens as the boundary ribbon and the
 * lineage trace — so a diagram reads as part of the instrument, never bolted on, and
 * inherits light/dark theming for free (the tokens flip with `data-theme`).
 *
 * Two grammars are accepted, so both our compact DSL and the Mermaid flowcharts a
 * model naturally emits render the same way:
 *
 *   Compact DSL (one statement per line):
 *     A: raw response            — name a node (id `A`, label "raw response")
 *     A -> B : interpret         — a flow edge, optional label after `:`
 *     A ≠ B                      — a boundary edge (also `!=`); rendered with ≠
 *
 *   Mermaid flowchart subset:
 *     graph TD / flowchart LR    — a leading direction header (ignored)
 *     A[Customer Profile]        — a node with a bracket label ([] () {} ([]) [[]] {{}})
 *     A --> B                    — a flow edge ( -->  ---  -.->  ==>  )
 *     A -->|label| B             — a flow edge with a label
 *     A; B; C                    — statements may be separated by `;` too
 *   subgraph / end / style / classDef / click lines are ignored.
 *
 * A node referenced in an edge is created on first sight with its id as its label.
 */
import type { ReactNode } from "react";

export type EdgeKind = "flow" | "ne";
export interface DiagramNode {
  readonly id: string;
  readonly label: string;
}
export interface DiagramEdge {
  readonly from: string;
  readonly to: string;
  readonly kind: EdgeKind;
  readonly label?: string;
}
export interface ParsedDiagram {
  readonly nodes: readonly DiagramNode[];
  readonly edges: readonly DiagramEdge[];
}

/**
 * Edge connector tokens. Mermaid's arrow family (`-->`, `---`, `-.->`, `==>`, `--x`,
 * `--o`) and our compact tokens, matched LONGEST-FIRST so `-->` wins over `->` and
 * `-.->` over `---`. `!=`/`≠` are boundary edges; everything else is a flow edge.
 */
const CONNECTORS: readonly { token: string; kind: EdgeKind }[] = [
  { token: "-.->", kind: "flow" },
  { token: "-.-", kind: "flow" },
  { token: "==>", kind: "flow" },
  { token: "===", kind: "flow" },
  { token: "--x", kind: "flow" },
  { token: "--o", kind: "flow" },
  { token: "-->", kind: "flow" },
  { token: "---", kind: "flow" },
  { token: "->", kind: "flow" },
  { token: "=>", kind: "flow" },
  { token: "~>", kind: "flow" },
  { token: "→", kind: "flow" },
  { token: "!=", kind: "ne" },
  { token: "≠", kind: "ne" },
];

/** Lone keywords (a whole line) that are not content — ignored. */
const KEYWORDS = new Set(["flow", "ladder", "lineage", "diagram", "mermaid"]);
/** Leading line prefixes from Mermaid we skip (header + decorations we don't render). */
const SKIP_PREFIX = /^(graph|flowchart|subgraph|end|style|classdef|class|click|linkstyle|direction)\b/i;

function stripQuotes(s: string): string {
  const t = s.trim();
  return (t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'")) ? t.slice(1, -1).trim() : t;
}

/**
 * Parse one Mermaid node token into {id, label}. Recognises `id`, `id[label]`,
 * `id(label)`, `id{label}`, `id([label])`, `id[[label]]`, `id[(label)]`, `id{{label}}`,
 * `id>label]`. With no bracket, the token is both id and label.
 */
function parseNode(tokenRaw: string): { id: string; label: string } {
  const token = tokenRaw.trim();
  const m = token.match(/^([A-Za-z0-9_.-]+)\s*(?:\[\[(.+)\]\]|\[\((.+)\)\]|\(\((.+)\)\)|\(\[(.+)\]\)|\{\{(.+)\}\}|\[(.+)\]|\((.+)\)|\{(.+)\}|>(.+)\])$/);
  if (m) {
    const label = m.slice(2).find((x) => x !== undefined);
    return { id: m[1]!, label: stripQuotes(label ?? m[1]!) };
  }
  return { id: token, label: token };
}

/** Pure: parse either grammar into ordered, de-duplicated nodes and edges. Never throws. */
export function parseDiagram(source: string): ParsedDiagram {
  const nodes = new Map<string, string>(); // id → label (insertion-ordered)
  const edges: DiagramEdge[] = [];
  const setNode = (id: string, label: string): void => {
    // A real label always wins over a bare-id placeholder created by an earlier edge.
    if (!nodes.has(id) || (nodes.get(id) === id && label !== id)) nodes.set(id, label);
  };

  // Statements may be separated by newlines OR `;` (Mermaid allows both).
  const statements = source.split("\n").flatMap((l) => l.split(";"));
  for (const raw of statements) {
    const line = raw.trim();
    if (!line) continue;
    if (KEYWORDS.has(line.toLowerCase())) continue;
    if (SKIP_PREFIX.test(line)) continue;

    // Find the first connector token present in the line.
    let found: { token: string; kind: EdgeKind; idx: number } | null = null;
    for (const c of CONNECTORS) {
      const idx = line.indexOf(c.token);
      if (idx >= 0 && (!found || idx < found.idx || (idx === found.idx && c.token.length > found.token.length))) {
        found = { ...c, idx };
      }
    }

    if (!found) {
      // No connector → a node definition. `id: label` (compact DSL), a Mermaid bracket
      // node `id[label]`, or a lone node name.
      const colon = line.indexOf(":");
      if (colon > 0 && !/[[({]/.test(line.slice(0, colon))) {
        const id = line.slice(0, colon).trim();
        if (id) setNode(id, line.slice(colon + 1).trim() || id);
      } else {
        const n = parseNode(line);
        setNode(n.id, n.label);
      }
      continue;
    }

    const leftTok = line.slice(0, found.idx).trim();
    let rest = line.slice(found.idx + found.token.length).trim();
    let label: string | undefined;
    // Edge label, either Mermaid `|label|` or our compact `: label`.
    const pipe = rest.match(/^\|([^|]*)\|\s*/);
    if (pipe) {
      label = pipe[1]!.trim() || undefined;
      rest = rest.slice(pipe[0].length).trim();
    } else {
      const colon = rest.indexOf(":");
      if (colon >= 0 && !/[[({]/.test(rest.slice(0, colon))) {
        label = rest.slice(colon + 1).trim() || undefined;
        rest = rest.slice(0, colon).trim();
      }
    }
    if (!leftTok || !rest) continue;
    const left = parseNode(leftTok);
    const right = parseNode(rest);
    setNode(left.id, left.label);
    setNode(right.id, right.label);
    edges.push({ from: left.id, to: right.id, kind: found.kind, ...(label ? { label } : {}) });
  }

  return {
    nodes: [...nodes].map(([id, label]) => ({ id, label })),
    edges,
  };
}

function NodeChip({ label }: { label: string }): JSX.Element {
  return <span className="diag-node">{label}</span>;
}

/** Render a parsed diagram. Edges drive the layout; lone nodes render as a chip strip. */
export function Diagram({ source }: { source: string }): JSX.Element | null {
  const { nodes, edges } = parseDiagram(source);
  if (nodes.length === 0) return null;
  const labelOf = (id: string): string => nodes.find((n) => n.id === id)?.label ?? id;

  if (edges.length === 0) {
    return (
      <div className="diagram" role="img" aria-label="diagram">
        <div className="diag-strip">
          {nodes.map((n) => (
            <NodeChip key={n.id} label={n.label} />
          ))}
        </div>
      </div>
    );
  }

  const aria = edges
    .map((e) => `${labelOf(e.from)} ${e.kind === "ne" ? "is not" : "to"} ${labelOf(e.to)}`)
    .join("; ");
  return (
    <div className="diagram" role="img" aria-label={aria}>
      {edges.map((e, i) => {
        const connector: ReactNode = (
          <span className={`diag-conn ${e.kind}`}>
            <span className="diag-glyph" aria-hidden="true">{e.kind === "ne" ? "≠" : "→"}</span>
            {e.label ? <span className="diag-label">{e.label}</span> : null}
          </span>
        );
        return (
          <div className="diag-edge" key={i}>
            <NodeChip label={labelOf(e.from)} />
            {connector}
            <NodeChip label={labelOf(e.to)} />
          </div>
        );
      })}
    </div>
  );
}
